import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { passwordProblem } from '../utils/passwordRules';
import { can } from '../utils/permissions';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import AccountRequests from '../components/users/AccountRequests';
import ManageAiCredits from '../components/users/ManageAiCredits';
import { isMetered } from '../utils/aiCredits';

// Per spec: no self-registration — Collection Manager creates all accounts
const ALL_ROLES = ['collection_manager', 'internal_expert', 'lab_technician', 'student', 'collector', 'lab_manager', 'read_only',
  // Phase 6 (migration 0035). Who may hold which scope: lambda/permissions.py.
  'authorized_researcher', 'iso_auditor', 'system_admin',
  // The read-only executive (0098): held alone (0099), granted by a system
  // admin only (permissions.grant_refusal), so offered to system admins only.
  'company_executive',
  // A researcher from outside the lab (0105): their own observations and AI,
  // nothing of the lab's (permissions.OUTSIDER_ROLES).
  'external_researcher'];
const EXECUTIVE = 'company_executive';

const EMPTY_FORM = {
  username: '',
  initials: '',
  email: '',
  password: '',
  roles: [],
};

const hasRole = (u, role) => Array.isArray(u.roles)
  ? u.roles.includes(role)
  : typeof u.roles === 'string' && u.roles.includes(role);

export default function Users() {
  const { t } = useTranslation(['users', 'common']);
  const { user, login } = useAuth();
  // routes/users.py: users:read lists the accounts; users:manage -- a
  // collection manager or a system admin -- changes them.
  const mayRead = can(user, 'users:read');
  const isManager = can(user, 'users:manage');
  const offeredRoles = ALL_ROLES.filter(r => r !== EXECUTIVE || (user?.roles || []).includes('system_admin'));

  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingUser, setDeletingUser]   = useState(false);
  const [deleteUserError, setDeleteUserError] = useState('');

  const [confirmReset, setConfirmReset]   = useState(null);
  const [resettingUser, setResettingUser] = useState(false);
  const [resetUserError, setResetUserError] = useState('');
  const [resetSuccessMsg, setResetSuccessMsg] = useState('');
  // Undoing an account's Google sign-in link (routes/users.py google-unlink)
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [unlinking, setUnlinking]         = useState(false);
  const [unlinkError, setUnlinkError]     = useState('');

  // Assignment modal state
  const [assignTarget, setAssignTarget]   = useState(null);
  // A metered account's AI credits (routes/ai_credits.py).
  const [creditsTarget, setCreditsTarget] = useState(null);
  const [assignList, setAssignList]       = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [collections, setCollections]     = useState([]);
  const [externals, setExternals]         = useState([]);
  const [assignSel, setAssignSel]         = useState('');
  const [assignSaving, setAssignSaving]   = useState(false);
  const [assignError, setAssignError]     = useState('');

  // A refusal the server named gets a sentence in the reader's language;
  // anything else falls back to what the server said, then to a generic line.
  const explain = (err, group, fallback) => {
    const data = err.response?.data || {};
    const key = data.code && `${group}.${data.code}`;
    if (key && t(key) !== key) {
      const until = data.hold_until ? new Date(data.hold_until).toLocaleString() : '';
      return t(key, { until });
    }
    return data.error || fallback;
  };

  const load = () => {
    setLoading(true);
    api.get('/users')
      .then(res => setUsers(res.data))
      .catch(err => setError(err.response?.data?.error || t('failedToLoad')))
      .finally(() => setLoading(false));
  };

  // Someone the page is not for is told so (below), and asks the server nothing.
  useEffect(() => { if (mayRead) load(); }, [mayRead]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const toggleRole = (role, target, setter) => {
    setter(prev => {
      const roles = Array.isArray(prev.roles) ? prev.roles : [];
      return {
        ...prev,
        // The executive role is held alone: choosing it clears the others,
        // and choosing another clears it.
        roles: roles.includes(role) ? roles.filter(r => r !== role)
          : role === EXECUTIVE ? [EXECUTIVE] : [...roles.filter(r => r !== EXECUTIVE), role],
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.username || !form.initials || !form.email || !form.password) {
      setFormError(t('createForm.usernameValidationError'));
      return;
    }
    if (form.roles.length === 0) {
      setFormError(t('createForm.roleValidationError'));
      return;
    }
    // The server holds a temporary password to the registration rules
    // (routes/users.py create_user); checked here first, in the same words.
    const problem = passwordProblem(form.password, form.username, form.email);
    if (problem) {
      setFormError(t(`createForm.errors.${problem}`));
      return;
    }
    setSubmitting(true);
    setFormError('');
    api.post('/users', { ...form })
      .then(() => { setShowForm(false); setForm(EMPTY_FORM); setShowPassword(false); load(); })
      .catch(err => setFormError(explain(err, 'createForm.errors', t('createForm.createFailed'))))
      .finally(() => setSubmitting(false));
  };

  const openEdit = (u) => {
    const roles = Array.isArray(u.roles)
      ? u.roles
      : typeof u.roles === 'string'
        ? u.roles.replace(/[{}]/g, '').split(',').map(r => r.trim()).filter(Boolean)
        : [];
    setEditTarget({ id: u.user_id, form: { username: u.username, email: u.email, roles, is_active: u.is_active } });
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditTarget(prev => ({ ...prev, form: { ...prev.form, [name]: type === 'checkbox' ? checked : value } }));
  };

  const submitEdit = (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    api.put(`/users/${editTarget.id}`, editTarget.form)
      .then(() => { setEditTarget(null); load(); })
      .catch(err => setEditError(explain(err, 'editModal.errors', t('editModal.updateFailed'))))
      .finally(() => setEditSaving(false));
  };

  const deleteUser = (userId) => {
    setDeletingUser(true);
    setDeleteUserError('');
    api.delete(`/users/${userId}`)
      .then(() => { setConfirmDelete(null); load(); })
      .catch(err => setDeleteUserError(
        explain(err, 'deleteModal.errors', t('deleteModal.deleteFailed'))))
      .finally(() => setDeletingUser(false));
  };

  const resetPassword = (userId) => {
    setResettingUser(true);
    setResetUserError('');
    // Its own endpoint, never a flag on the edit: one request must not be
    // able to change the address AND have a reset link sent to it.
    api.post(`/users/${userId}/password-reset`)
      .then(() => {
        setResetSuccessMsg(t('resetPasswordModal.resetSuccess', { email: confirmReset.email }));
        setConfirmReset(null);
      })
      .catch(err => setResetUserError(
        explain(err, 'resetPasswordModal.errors', t('resetPasswordModal.resetFailed'))))
      .finally(() => setResettingUser(false));
  };

  const unlinkGoogle = (target) => {
    setUnlinking(true);
    setUnlinkError('');
    api.post(`/users/${target.user_id}/google-unlink`)
      .then((res) => {
        // Unlinking ends every session the account holds. On your own
        // account the server sends this session back at the new version
        // (same expiry); without it the next request would sign you out.
        if (res.data?.token) login(res.data.token);
        setResetSuccessMsg(t('unlinkGoogleModal.success', { name: target.username }));
        setConfirmUnlink(null);
        load();
      })
      .catch(err => setUnlinkError(
        explain(err, 'unlinkGoogleModal.errors', t('unlinkGoogleModal.failed'))))
      .finally(() => setUnlinking(false));
  };

  const openAssign = (u) => {
    setAssignTarget(u);
    setAssignSel('');
    setAssignError('');
    setAssignLoading(true);
    Promise.all([
      api.get('/assignments'),
      api.get('/collections'),
      api.get('/external'),
    ]).then(([aRes, cRes, eRes]) => {
      setAssignList(aRes.data.filter(a => a.student_id === u.user_id));
      setCollections(cRes.data);
      setExternals(eRes.data);
    }).catch(() => setAssignError(t('assignModal.loadDataFailed')))
      .finally(() => setAssignLoading(false));
  };

  const submitAssign = (e) => {
    e.preventDefault();
    if (!assignSel) return;
    const [type, id] = assignSel.split(':');
    const payload = {
      student_id: assignTarget.user_id,
      ...(type === 'fc' ? { sample_id: id } : { external_id: id }),
    };
    setAssignSaving(true);
    setAssignError('');
    api.post('/assignments', payload)
      .then(res => {
        const qr = [...collections, ...externals].find(
          c => (c.sample_id || c.external_id) === id
        )?.qr_uid || id.slice(0, 8);
        setAssignList(l => [...l, {
          assignment_id: res.data.assignment_id,
          student_id: assignTarget.user_id,
          sample_id: type === 'fc' ? id : null,
          external_id: type === 'ext' ? id : null,
          sample_qr: qr,
        }]);
        setAssignSel('');
      })
      .catch(err => setAssignError(err.response?.data?.error || t('assignModal.assignFailed')))
      .finally(() => setAssignSaving(false));
  };

  const removeAssignment = (assignmentId) => {
    api.delete(`/assignments/${assignmentId}`)
      .then(() => setAssignList(l => l.filter(a => a.assignment_id !== assignmentId)))
      .catch(() => {});
  };

  const formatRoles = (roles, t) => {
    const list = Array.isArray(roles)
      ? roles
      : typeof roles === 'string'
        ? roles.replace(/[{}]/g, '').split(',').map(r => r.trim()).filter(Boolean)
        : null;
    if (!list) return '—';
    return list.map(r => t(`role.${r}`, { defaultValue: r })).join(', ');
  };

  if (!mayRead) {
    return <p style={{ color: 'var(--c-text-muted-fg)' }}>{t('accessRestricted')}</p>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{t('pageTitle')}</h2>
        {isManager && (
          <button onClick={() => { setShowForm(s => !s); setFormError(''); }} title={showForm ? t('tip.cancelNewUser') : t('tip.newUser')} style={btnStyle('var(--c-primary)')}>
            {showForm ? t('common:cancel') : t('newUserBtn')}
          </button>
        )}
      </div>

      {resetSuccessMsg && (
        <p style={{ color: 'var(--c-success-fg)', background: 'var(--c-success-soft)', borderRadius: 6, padding: '8px 12px', marginBottom: 16 }}>
          {resetSuccessMsg}
        </p>
      )}

      {/* Who registered or asked for more access, waiting for a decision
          (routes/account_requests.py, migration 0106). */}
      <AccountRequests onChanged={load} readOnly={!isManager} roles={offeredRoles} />

      {isManager && showForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          <h3 style={{ marginTop: 0 }}>{t('createForm.title')}</h3>
          <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 16px' }}>
            {t('createForm.noSelfRegNotice')}
          </p>
          {formError && <p style={{ color: 'red', margin: '0 0 12px' }}>{formError}</p>}
          <div style={gridStyle}>
            <label style={labelStyle}>
              {t('createForm.usernameLabel')}
              <input name="username" value={form.username} onChange={handleChange} title={t('tip.username')} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              {t('createForm.initialsLabel')}
              <input name="initials" value={form.initials} onChange={handleChange}
                placeholder={t('createForm.initialsPlaceholder')} maxLength={10} title={t('tip.initials')} style={inputStyle} />
            </label>
            <label style={labelStyle}>
              {t('createForm.emailLabel')}
              <input name="email" type="email" value={form.email} onChange={handleChange} title={t('tip.email')} style={inputStyle} dir="ltr" />
            </label>
            <label style={labelStyle}>
              {t('createForm.passwordLabel')}
              <div style={{ position: 'relative' }}>
                <input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange}
                  title={t('tip.password')} style={{ ...inputStyle, paddingInlineEnd: 40 }} />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  title={showPassword ? t('tip.hidePassword') : t('tip.showPassword')}
                  aria-label={showPassword ? t('createForm.hidePassword') : t('createForm.showPassword')}
                  style={{
                    position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 15,
                    color: 'var(--c-text-muted-fg)', padding: 4, lineHeight: 1,
                  }}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </label>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{t('createForm.rolesLabel')} <span style={{ fontWeight: 400, color: 'var(--c-text-muted-fg)' }}>{t('createForm.rolesHint')}</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {offeredRoles.map(role => (
                <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(role)}
                    onChange={() => toggleRole(role, form, setForm)}
                    title={t('tip.roleCheckbox')}
                  />
                  {t(`role.${role}`)}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" disabled={submitting} title={t('tip.createUser')} style={btnStyle('var(--c-primary)')}>
            {submitting ? t('createForm.creating') : t('createForm.createBtn')}
          </button>
        </form>
      )}

      {loading && <p>{t('common:loading')}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}

      {!loading && !error && (
        users.length === 0 ? (
          <p>{t('noUsersFound')}</p>
        ) : (
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr style={{ background: 'var(--c-bg-muted-2)' }}>
                {[t('table.username'), t('table.email'), t('table.roles'), t('table.active'), t('table.created'), t('table.actions')].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} style={{ borderBottom: '1px solid var(--c-bg-muted-3)' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{u.username}</td>
                  <td style={tdStyle} dir="ltr">{u.email}</td>
                  <td style={{ ...tdStyle, fontSize: 13 }}>{formatRoles(u.roles, t)}</td>
                  <td style={tdStyle}>
                    <span style={{
                      background: u.is_active ? 'var(--c-success-soft)' : 'var(--c-danger-soft)',
                      color:      u.is_active ? 'var(--c-success-fg)' : 'var(--c-danger-2-fg)',
                      padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>
                      {u.is_active ? t('active') : t('inactive')}
                    </span>
                    {/* A self-registered account that has not proved its
                        address yet: activating it would still not let it
                        sign in, so managers are shown why. */}
                    {u.email_verified === false && (
                      <span style={{
                        marginInlineStart: 6, background: 'var(--c-warning-soft)', color: 'var(--c-warning-text-fg)',
                        padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      }}>
                        {t('emailUnverified')}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    {isManager && (<>
                    <button onClick={() => openEdit(u)} title={t('tip.edit')} style={smallBtn('var(--c-info)')}>{t('actions.edit')}</button>
                    {' '}
                    {hasRole(u, 'student') && (
                      <button onClick={() => openAssign(u)} title={t('tip.assign')} style={smallBtn('var(--c-info-6)')}>{t('actions.assign')}</button>
                    )}
                    {' '}
                    {/* Your own account resets through "Forgot password?"; the
                        server refuses this path for it (use_self_service_reset). */}
                    {u.user_id !== user?.user_id && (
                      <button data-testid={`reset-${u.user_id}`} onClick={() => { setResetUserError(''); setConfirmReset(u); }} title={t('tip.resetPassword')} style={smallBtn('var(--c-warning-text-2)')}>{t('actions.resetPassword')}</button>
                    )}
                    {' '}
                    {/* Signs in with Google (0052): a way in no password reset
                        touches, so a manager can undo it on its own. */}
                    {u.google_linked && (
                      <button data-testid={`unlink-google-${u.user_id}`}
                        onClick={() => { setUnlinkError(''); setConfirmUnlink(u); }}
                        title={t('tip.unlinkGoogle')} style={smallBtn('var(--c-warning-text-2)')}>
                        {t('actions.unlinkGoogle')}
                      </button>
                    )}
                    {' '}
                    {isMetered(u) && (
                      <button data-testid={`ai-credits-${u.user_id}`} onClick={() => setCreditsTarget(u)}
                        title={t('tip.aiCredits')} aria-haspopup="dialog" style={smallBtn('var(--c-info-6)')}>
                        {t('actions.aiCredits')}
                      </button>
                    )}
                    {' '}
                    {u.user_id !== user?.user_id && (
                      <button onClick={() => { setDeleteUserError(''); setConfirmDelete(u); }} title={t('tip.delete')} style={smallBtn('var(--c-danger-2)')}>{t('actions.delete')}</button>
                    )}
                    </>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}

      {/* Edit modal */}
      {creditsTarget && <ManageAiCredits account={creditsTarget} onClose={() => { setCreditsTarget(null); load(); }} />}

      {editTarget && (
        <div style={overlayStyle} onClick={() => setEditTarget(null)}>
          <div role="dialog" aria-modal="true" style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{t('editModal.title')}</h3>
            {editError && <p style={{ color: 'red', margin: '0 0 12px' }}>{editError}</p>}
            <form onSubmit={submitEdit}>
              <label style={{ ...labelStyle, marginBottom: 12 }}>
                {t('editModal.usernameLabel')}
                <input name="username" value={editTarget.form.username} onChange={handleEditChange} title={t('tip.editUsername')} style={inputStyle} />
              </label>
              <label style={{ ...labelStyle, marginBottom: 12 }}>
                {t('editModal.emailLabel')}
                <input name="email" type="email" value={editTarget.form.email} onChange={handleEditChange} title={t('tip.editEmail')} style={inputStyle} dir="ltr" />
                {editTarget.id !== user?.user_id && (
                  <span data-testid="email-change-note" style={{ display: 'block', fontSize: 12, color: 'var(--c-text-muted-fg)', marginTop: 4 }}>
                    {t('editModal.emailChangeNote')}
                  </span>
                )}
              </label>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{t('editModal.rolesLabel')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {offeredRoles.map(role => (
                    <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(editTarget.form.roles || []).includes(role)}
                        onChange={() => toggleRole(role, editTarget.form, (updater) =>
                          setEditTarget(prev => ({ ...prev, form: updater(prev.form) }))
                        )}
                        title={t('tip.editRoleCheckbox')}
                      />
                      {t(`role.${role}`)}
                    </label>
                  ))}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16 }}>
                <input type="checkbox" name="is_active" checked={editTarget.form.is_active} onChange={handleEditChange} title={t('tip.active')} />
                {t('editModal.activeLabel')}
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={editSaving} title={t('tip.saveEdit')} style={btnStyle('var(--c-primary)')}>
                  {editSaving ? t('common:saving') : t('common:save')}
                </button>
                <button type="button" onClick={() => setEditTarget(null)} title={t('tip.cancelEdit')} style={btnStyle('var(--c-text-muted)')}>{t('common:cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment modal */}
      {assignTarget && (
        <div style={overlayStyle} onClick={() => setAssignTarget(null)}>
          <div role="dialog" aria-modal="true" style={{ ...modalStyle, width: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>{t('assignModal.title', { username: assignTarget.username })}</h3>
              <button onClick={() => setAssignTarget(null)} title={t('tip.closeAssign')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--c-text-muted-fg)' }}>×</button>
            </div>

            {assignError && <p style={{ color: 'red', margin: '0 0 12px', fontSize: 13 }}>{assignError}</p>}

            {assignLoading ? <p>{t('common:loading')}</p> : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-muted-fg)', marginBottom: 8 }}>{t('assignModal.currentAssignmentsHeader')}</div>
                  {assignList.length === 0 ? (
                    <p style={{ fontSize: 13, color: 'var(--c-text-faint-fg)', margin: 0 }}>{t('assignModal.noAssignmentsYet')}</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {assignList.map(a => (
                        <div key={a.assignment_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--c-bg-muted)', borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
                          <span>
                            <span style={{ fontFamily: 'monospace' }} dir="ltr">{a.sample_qr || (a.sample_id || a.external_id)?.slice(0, 8) + '…'}</span>
                            <span style={{ color: 'var(--c-text-muted-fg)', marginInlineStart: 8 }}>{a.sample_id ? t('assignModal.fieldLabel') : t('assignModal.externalLabel')}</span>
                          </span>
                          <button onClick={() => removeAssignment(a.assignment_id)} title={t('tip.removeAssignment')} style={smallBtn('var(--c-danger-2)')}>{t('assignModal.removeBtn')}</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <form onSubmit={submitAssign}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-muted-fg)', marginBottom: 8 }}>{t('assignModal.addAssignmentHeader')}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      value={assignSel}
                      onChange={e => setAssignSel(e.target.value)}
                      title={t('tip.assignSelect')}
                      style={{ ...inputStyle, flex: 1 }}
                    >
                      <option value="">{t('assignModal.selectSamplePlaceholder')}</option>
                      {collections.length > 0 && (
                        <optgroup label={t('assignModal.fieldCollectionsGroup')}>
                          {collections
                            .filter(c => !assignList.some(a => a.sample_id === c.sample_id))
                            .map(c => (
                              <option key={c.sample_id} value={`fc:${c.sample_id}`}>
                                {c.qr_uid || c.sample_id.slice(0, 8)} — {c.current_taxon_name || t('assignModal.unknownTaxon')} ({c.collection_datetime?.slice(0,4) || '?'})
                              </option>
                            ))}
                        </optgroup>
                      )}
                      {externals.length > 0 && (
                        <optgroup label={t('assignModal.externalCollectionsGroup')}>
                          {externals
                            .filter(e => !assignList.some(a => a.external_id === e.external_id))
                            .map(e => (
                              <option key={e.external_id} value={`ext:${e.external_id}`}>
                                {e.qr_uid || e.external_id.slice(0, 8)} — {e.taxon_name || t('assignModal.unknownTaxon')}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </select>
                    <button type="submit" disabled={assignSaving || !assignSel} title={t('tip.assignSubmit')} style={btnStyle('var(--c-info-6)')}>
                      {assignSaving ? t('common:saving') : t('assignModal.assignBtn')}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={!!confirmDelete}
        title={t('deleteModal.title')}
        consequences={confirmDelete ? [
          t('deleteModal.confirmText', { name: confirmDelete.username }),
          t('deleteModal.deleteConsequence1'),
          t('deleteModal.deleteConsequence2'),
        ] : []}
        deleting={deletingUser}
        error={deleteUserError}
        onConfirm={() => deleteUser(confirmDelete.user_id)}
        onCancel={() => setConfirmDelete(null)}
      />

      <ConfirmDeleteModal
        open={!!confirmReset}
        title={t('resetPasswordModal.title')}
        consequences={confirmReset ? [
          t('resetPasswordModal.confirmText', { name: confirmReset.username, email: confirmReset.email }),
          t('resetPasswordModal.consequence1'),
          t('resetPasswordModal.consequence2'),
          ...(confirmReset.google_linked ? [t('resetPasswordModal.consequenceGoogle')] : []),
        ] : []}
        deleting={resettingUser}
        error={resetUserError}
        confirmLabel={t('resetPasswordModal.confirmBtn')}
        confirmingLabel={t('resetPasswordModal.sending')}
        confirmColor="var(--c-warning-text-2)"
        onConfirm={() => resetPassword(confirmReset.user_id)}
        onCancel={() => setConfirmReset(null)}
      />

      <ConfirmDeleteModal
        open={!!confirmUnlink}
        title={t('unlinkGoogleModal.title')}
        consequences={confirmUnlink ? [
          t('unlinkGoogleModal.confirmText', { name: confirmUnlink.username }),
          t('unlinkGoogleModal.consequence1'),
          confirmUnlink.user_id === user?.user_id
            ? t('unlinkGoogleModal.consequenceSessionsOwn')
            : t('unlinkGoogleModal.consequenceSessions'),
        ] : []}
        deleting={unlinking}
        error={unlinkError}
        confirmLabel={t('unlinkGoogleModal.confirmBtn')}
        confirmingLabel={t('unlinkGoogleModal.unlinking')}
        confirmColor="var(--c-warning-text-2)"
        onConfirm={() => unlinkGoogle(confirmUnlink)}
        onCancel={() => setConfirmUnlink(null)}
      />
    </div>
  );
}

const thStyle = { padding: '8px 12px', textAlign: 'start', fontWeight: 600 };
const tdStyle = { padding: '8px 12px' };

const formStyle = {
  background: 'var(--c-bg-subtle)',
  border: '1px solid var(--c-border)',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 14,
  marginBottom: 16,
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 14,
  fontWeight: 500,
};

const inputStyle = {
  padding: '6px 10px',
  border: '1px solid var(--c-border-strong)',
  borderRadius: 6,
  fontSize: 14,
  fontWeight: 400,
};

const btnStyle = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 14,
});

const smallBtn = (bg) => ({
  background: bg,
  color: 'var(--c-on-accent)',
  border: 'none',
  borderRadius: 4,
  padding: '3px 10px',
  cursor: 'pointer',
  fontSize: 12,
});

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: 'var(--c-surface)',
  borderRadius: 10,
  padding: 28,
  width: 460,
  maxWidth: '90vw',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
